import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Info, X } from 'lucide-react';
import type { PrintableColor } from '@customedge/shared';
import { nearestPantone } from '@customedge/shared';

// Shared by every color-selection surface (BackgroundColorTool.tsx's
// per-swatch editor, ColorPicker.tsx's generic text/stroke/fill picker,
// PropertyPanel.tsx, ColorsUsedPanel.tsx) per direct user request that PMS
// show up "on all color selection palette[s]". `nearestPantone` itself now
// lives in @customedge/shared/utils/printColors.ts (re-exported here for
// existing call sites) — the Design Guide PDF and the admin Order Detail
// page need the identical nearest-PMS-match logic against the same
// admin-maintained PrintableColor library, so it was moved out of this
// frontend-only file rather than duplicated a third/fourth time.
export { nearestPantone };

// "What is Pantone Matching System?" explainer, opened from the (i) info
// icon next to the PMS code field — mirrors the reference tool's popup
// content/structure (4 example PMS chip cards + the two explanatory
// paragraphs, including its CMYK/RGB/HEX-has-no-exact-match caveat).
export function PmsInfoPopup({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface, white)', borderRadius: 12, padding: 24, maxWidth: 480,
          width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{t('pms.infoTitle')}</h3>
          <button onClick={onClose} style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{
          background: 'var(--surface-secondary, #f3f4f6)', borderRadius: 8, padding: 16,
          display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', justifyContent: 'center',
        }}>
          {[
            { code: '1788 C', hex: '#ED1C3F' },
            { code: '158 C', hex: '#ED7D31' },
            { code: '7689 C', hex: '#1F6FB2' },
            { code: '2975 C', hex: '#9FD4E8' },
          ].map(p => (
            <div key={p.code} style={{ width: 78, fontSize: 10, textAlign: 'center' }}>
              <div style={{ width: '100%', height: 56, background: p.hex, borderRadius: '4px 4px 0 0' }} />
              <div style={{ background: 'white', border: '1px solid var(--border)', borderTop: 0, borderRadius: '0 0 4px 4px', padding: '4px 2px' }}>
                <div style={{ fontWeight: 600 }}>PANTONE®</div>
                <div>{p.code}</div>
              </div>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)', margin: '0 0 12px' }}>
          {t('pms.infoParagraph1')}
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)', margin: 0 }}>
          {t('pms.infoParagraph2')}
        </p>

        <button
          onClick={onClose}
          style={{
            marginTop: 20, width: '100%', padding: '10px 16px', borderRadius: 8, border: 0,
            background: 'var(--selection)', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}
        >
          {t('pms.gotIt')}
        </button>
      </div>
    </div>,
    document.body,
  );
}

// Labeled section — title ("Pantone Matching System (PMS)") with the (i)
// info icon right after it, then a single row holding one normal-size
// (18x18, matching every other "Your colors"/"Select from logo" swatch row
// in this app) square swatch + the PMS code as text beside it. Previously a
// compact inline pill with a 10px circle dot — per direct user request
// ("PMS color preview is very small circle instead show like 'Your
// colors'... No title is there... add the info icon after title"), redone
// to match this app's own established "titled row of swatches" convention
// (see ColorsUsedPanel.tsx's "Your colors"/"Colors used in this design" rows,
// and this same file's own logoColors/designColors rows) rather than reading
// as a one-off compact badge. Still only ever shows the SINGLE nearest match
// (nearestPantone) — there is exactly one row/swatch here, not a browse-all
// grid (that grid was removed entirely per an earlier fix once the admin's
// real ~1300-color PMS library made it unusably large).
export function PmsReadout({ match }: { match: PrintableColor | null }) {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('pms.title')}</span>
        <button
          type="button"
          onClick={() => setInfoOpen(true)}
          title={t('pms.infoTitle')}
          style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--text-muted)' }}
        >
          <Info size={13} />
        </button>
      </div>
      {match ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 18, height: 18, borderRadius: 3, background: match.colorCode, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
          <span style={{ fontSize: 12 }}>{t('pms.matchLabel', { code: match.pantoneColor })}</span>
        </div>
      ) : (
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('pms.noMatch')}</span>
      )}
      {infoOpen && <PmsInfoPopup onClose={() => setInfoOpen(false)} />}
    </div>
  );
}
